// src/hooks/useVisibilityPoll.js
//
// Runs `callback` on an interval, pausing whenever the tab is hidden
// and restarting (with an immediate kick) when it becomes visible
// again. Saves battery and cuts server load on backgrounded tabs.
//
// Usage:
//   useVisibilityPoll(() => loadUnread(), 30000);

import { useEffect, useRef } from 'react';

export function useVisibilityPoll(callback, intervalMs = 30000, enabled = true) {
  const savedCb = useRef(callback);
  savedCb.current = callback;

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return undefined;
    let timer = null;

    const run = () => savedCb.current?.();

    const start = () => {
      stop();
      run();
      timer = setInterval(run, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, enabled]);
}

export default useVisibilityPoll;
